import * as React from "react";
import {connect} from "react-redux";
import {option, Option} from "ts-option";
import {RootState} from "../../../store/reducer/root/RootState";
import JobType from "../../../model/job/JobType";
import Utility from "../../../model/utility/Utility";
import {fetchUtilities} from "../../../store/action/utility/utility";
import {isDefined} from "../../../utils/Utils";
import {toKeyIndexedObject} from "../../../utils/ArrayUtils";
import Row from "../../layout/Row";
import Col from "../../layout/Col";
import Switch from "../../../hiberUiKit/src/atoms/Switch/Switch";
import RangeNumberInput from "../../../hiberUiKit/src/atoms/Input/RangeNumberInput";
import SelectUtility from "../utilities/form/SelectUtility";
import FormGroup from "../../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import "../shared/jobTypeModal.scss";
import "./partnerBrandJobTypesCreate.scss";

interface ComponentProps {
	/**
	 * Editable job types list (controlled). Active state, fee and utility are edited in place.
	 */
	jobTypes: JobType[];
	/**
	 * Reports the updated job types list on every change.
	 * @param jobTypes updated list.
	 */
	onChange: (jobTypes: JobType[]) => void;
	/**
	 * Section level validation error (e.g. when none selected).
	 */
	error?: string | null;
}

interface ReduxProps {
	/**
	 * Globally configured job types, keyed by id. Used to warn about globally disabled job types.
	 */
	jobTypesMap: {[key: string]: JobType};
	dispatch: any;
}

type Props = ComponentProps & ReduxProps;

/**
 * Full job type configuration (active / fee / utility) used while creating a partner brand.
 * Mirrors the configuration available when editing a partner brand. Defaults to none selected;
 * at least one job type must be active and every active job type must have a fee.
 */
class PartnerBrandJobTypesConfig extends React.Component<Props> {
	componentDidMount() {
		this.props.dispatch(fetchUtilities());
	}

	render() {
		return (
			<div className={"c-create-brand c-pb-job-types"}>
				<FormGroup label={"Job types"} error={this.props.error}>
					<p className={"c-pb-job-types_hint"}>Select at least one job type to enable for this partner brand and set its fee.</p>
					<div className={"c-job-type-modal-table"}>
						<Row customClass={"header"}>
							<Col xl={3} md={3}>Name</Col>
							<Col xl={2} md={2}>Active</Col>
							<Col xl={2} md={2}>Fee</Col>
							<Col xl={3} md={3}>Utility</Col>
							<Col xl={2} md={2}/>
						</Row>
						{this.props.jobTypes.map(jt => {
							const globalJT = this.props.jobTypesMap[jt.id];
							const showWarning = jt.active && globalJT && !globalJT.active;
							const showRequiredWarning = jt.active && !isDefined(jt.fee);

							return <Row key={jt.id}>
								<Col xl={3} md={3}>{jt.name}</Col>
								<Col xl={2} md={2}>
									<Switch checked={jt.active}
											onChange={value => this.changeJobTypeActiveProperty(jt.id, value)}/>
								</Col>
								<Col xl={2} md={2}>
									<RangeNumberInput
										onChange={value => this.changeJobTypeFeeProperty(jt.id, value)}
										value={option(jt.fee)}
										minValue={option(0)}
										maxValue={option(100)}
										disabled={!jt.active}
										maxDecimalPlaces={2}/>
								</Col>
								<Col xl={3} md={3}>
									<SelectUtility
										placeholder={"Brand default"}
										selectedId={option(jt.utilityId)}
										onChanged={(u: Option<Utility>) => this.changeJobTypeUtilityProperty(jt.id, u)}
										clearable={true}
										portalMenu={true}/>
								</Col>
								<Col xl={2} md={2}>
									<span className={"c-job-type-modal-table_warning"}>
										{showWarning && <span className={"text"}>This job type is globally disabled!</span>}
										{showRequiredWarning &&
										<span className={"text"}>Fee is required. Please fill the input.</span>}
									</span>
								</Col>
							</Row>
						})}
					</div>
				</FormGroup>
			</div>
		);
	}

	changeJobTypeActiveProperty(jobTypeId: string, active: boolean) {
		this.emitChange(jobTypeId, jt => jt.copy({active, fee: isDefined(jt.fee) ? jt.fee : 0}));
	}

	changeJobTypeFeeProperty(jobTypeId: string, newValue: Option<number>) {
		this.emitChange(jobTypeId, jt => jt.copy({fee: newValue.orNull}));
	}

	changeJobTypeUtilityProperty(jobTypeId: string, utility: Option<Utility>) {
		this.emitChange(jobTypeId, jt => jt.withUtility(utility.isDefined ? utility.get.id : undefined));
	}

	private emitChange(jobTypeId: string, transform: (jt: JobType) => JobType) {
		this.props.onChange(this.props.jobTypes.map(jt => jt.id === jobTypeId ? transform(jt) : jt));
	}
}

function mapStateToProps(state: RootState) {
	const jobTypes = state.jobType.jobTypes.getOrElse([]);
	return {
		jobTypesMap: toKeyIndexedObject(jobTypes, jt => jt.id)
	}
}

export default connect(mapStateToProps)(PartnerBrandJobTypesConfig);
